import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, RichTextDisplay, cn, Z_INDEX } from "@tellescope/ui";
import { X } from "lucide-react";
import { POPUP_VARIANT_STYLES } from "../schema";
import type { PopupBlock } from "../schema";

interface PopupDialogProps {
  open: boolean;
  onClose: () => void;
  content: string;
  variant: PopupBlock["props"]["variant"];
  size?: PopupBlock["props"]["size"];
  title?: string;
  customBackgroundColor?: string;
  customTextColor?: string;
  /** Additional z-index offset for layering (e.g., +1 for editor preview) */
  zIndexOffset?: number;
}

export const PopupDialog = ({
  open,
  onClose,
  content,
  variant,
  size = "default",
  title,
  customBackgroundColor,
  customTextColor,
  zIndexOffset = 1,
}: PopupDialogProps) => {
  const { t } = useTranslation();

  const isCustomVariant = variant === "custom";
  const backgroundStyle =
    isCustomVariant && customBackgroundColor
      ? { backgroundColor: customBackgroundColor, color: customTextColor || "inherit" }
      : undefined;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        className={cn("border-2", size === "wide" && "sm:max-w-2xl", !isCustomVariant && POPUP_VARIANT_STYLES[variant])}
        style={{ ...backgroundStyle, "--z": Z_INDEX.modal + zIndexOffset } as React.CSSProperties}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
          aria-label={t("Close")}
        >
          <X className="h-4 w-4" />
        </button>
        {title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        <RichTextDisplay
          key={content}
          content={content || `<p>${t("No content")}</p>`}
          className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
        />
      </DialogContent>
    </Dialog>
  );
};
